import React, { useMemo } from "react";

import { Edit2, Trash2 } from "lucide-react";

import { Button } from "~/components/ui/button";
import { OrderItem } from "~/types/product";
import {
  FLETE_AUTO_ADD_ENABLED,
  FLETE_PRODUCT_ID,
} from "~/utils/fleteUtils";
import { formatCurrency } from "~/utils/helpers";
import { calcLineTotal } from "~/utils/lineItemCalculations";

interface OrderSummaryProps {
  items: OrderItem[];
  onRemoveItem: (itemId: string) => void;
  onEditItem: (item: OrderItem) => void;
  total: number;
}

const OrderSummary: React.FC<OrderSummaryProps> = ({
  items,
  onRemoveItem,
  onEditItem,
  total,
}) => {
  // Sort items so FLETE always appears last
  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      const aIsFlete = a.product.id === FLETE_PRODUCT_ID;
      const bIsFlete = b.product.id === FLETE_PRODUCT_ID;
      if (aIsFlete && !bIsFlete) return 1;
      if (!aIsFlete && bIsFlete) return -1;
      return 0;
    });
  }, [items]);

  return (
    <div className="space-y-4">
      <div className="overflow-hidden rounded-lg border">
        <div className="max-h-48 overflow-y-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="sticky top-0 bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                >
                  Producto
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500"
                >
                  Cantidad
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500"
                >
                  Precio
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500"
                >
                  Dcto. %
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500"
                >
                  Subtotal
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500"
                >
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {sortedItems.map((item) => (
                <tr key={item.id || item.product.id}>
                  <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">
                    {item.product.short_name}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-500">
                    {item.quantity}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-500">
                    {formatCurrency(item.unitPrice)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-500">
                    {item.discountPercent ? `${item.discountPercent}%` : "-"}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium text-gray-900">
                    {formatCurrency(
                      calcLineTotal(
                        item.unitPrice,
                        item.quantity,
                        item.discountPercent ?? 0
                      )
                    )}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium">
                    {FLETE_AUTO_ADD_ENABLED &&
                    item.product.id === FLETE_PRODUCT_ID ? (
                      <span className="text-xs text-gray-400">Auto</span>
                    ) : (
                      <div className="flex justify-end space-x-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onEditItem(item)}
                          title="Editar"
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onRemoveItem(item.id || item.product.id)}
                          title="Eliminar"
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex justify-between border-t pt-4">
        <span className="font-semibold">Total</span>
        <span className="text-lg font-bold">{formatCurrency(total)}</span>
      </div>
    </div>
  );
};

export default OrderSummary;
